import { useCallback, useEffect, useReducer, useRef } from 'react'

import {
  applyAction,
  createRng,
  mastermindEngine,
  toMastermindView,
  type MastermindAction,
  type MastermindSize,
  type MastermindState,
  type MastermindView,
  type Peg,
  type SkillLevels,
} from '@hazel/shared'

import { api } from '../api/client.js'
import type { DealtRound } from './useRound.js'
import { useMoveLog } from './useMoveLog.js'
import { useMilestone } from './useMilestone.js'

const SOLO = 'solo'
const PLAYER = { id: SOLO, displayName: 'You', userId: null } as const

interface Round {
  readonly state: MastermindState
}

interface RoundEvent {
  readonly action: MastermindAction
  readonly now: number
}

function start(seed: string, size: MastermindSize, levels: SkillLevels, now: number): Round {
  return {
    state: mastermindEngine.createInitialState(
      { size, levels, startedAt: now },
      [PLAYER],
      createRng(seed),
    ),
  }
}

/** Pure, so React re-running it cannot record a round twice. */
function reduceRound(round: Round, event: RoundEvent): Round {
  const outcome = applyAction(mastermindEngine, round.state, event.action, {
    playerId: SOLO,
    now: event.now,
  })

  // A refused move leaves the round exactly as it was, here and on the server.
  return outcome.ok ? { state: outcome.state } : round
}

export interface Mastermind {
  readonly view: MastermindView
  /** Put a teddy in one place of the row she is building. */
  readonly place: (at: number, peg: Peg) => void
  /** Take one back out. */
  readonly take: (at: number) => void
  /** Clear the row without spending a go. */
  readonly clear: () => void
  /** Spend a go on the row as it stands. */
  readonly tryIt: () => void
  readonly again: () => void
  readonly finished: boolean
  readonly milestone: number | null
}

export interface MastermindConfigFromRound {
  readonly size: MastermindSize
  readonly levels: SkillLevels
}

/**
 * Chaka's Mastermind, running the shared engine in the browser.
 *
 * **This tab knows the answer**, and there is no way around that: it has to
 * answer her guesses, and an answer needs the row. So the protection is not
 * secrecy but arithmetic — `toMastermindView` withholds the row from what the
 * screen renders, and the server re-derives the whole round from the seed it
 * issued rather than believing a word of what is sent back. Nothing here is
 * worth forging anyway, which is the point the engine's own header makes:
 * making a round look better only means being asked harder ones next time.
 */
export function useMastermind(
  round: DealtRound<MastermindConfigFromRound>,
  onFinished: () => void,
): Mastermind {
  const [state, dispatch] = useReducer(reduceRound, round.seed, (seed) =>
    start(seed, round.config.size, round.config.levels, Date.now()),
  )

  const moves = useMoveLog(round.roundId)

  const award = useMilestone()
  const recorded = useRef(false)
  const finished = state.state.status !== 'playing'

  useEffect(() => {
    if (!finished || recorded.current) return
    recorded.current = true

    void moves
      .flush()
      .then((told) => {
        // Nothing to score if the server never heard the whole round. The
        // round is not spent, so it can still be finished from another device.
        if (!told) return undefined

        return api('/api/plays/mastermind', {
          method: 'POST',
          body: JSON.stringify({ roundId: round.roundId }),
        })
      })
      .then(award.notice)
      // A failure to record must not interrupt anything. She worked the row
      // out either way.
      .catch(() => undefined)
  }, [finished, round.roundId, moves, award.notice])

  const send = useCallback(
    (action: MastermindAction) => {
      const now = Date.now()

      dispatch({ action, now })
      // Never earlier than the move before it.
      moves.record(action, Math.max(0, now - state.state.startedAt))
    },
    [moves, state.state.startedAt],
  )

  return {
    view: toMastermindView(state.state),
    place: useCallback(
      (at: number, peg: Peg) => {
        send({ type: 'place', at, peg })
      },
      [send],
    ),
    take: useCallback(
      (at: number) => {
        send({ type: 'take', at })
      },
      [send],
    ),
    clear: useCallback(() => {
      send({ type: 'clear' })
    }, [send]),
    /*
     * Spending a go, which is the only move that cannot be taken back.
     *
     * Sent like any other, so the server sees the same ten decisions in the
     * same order — it is what lets a round be re-derived rather than trusted.
     */
    tryIt: useCallback(() => {
      send({ type: 'try' })
    }, [send]),
    again: onFinished,
    finished,
    milestone: award.milestone,
  }
}
